import React, { useCallback, useEffect, useState } from "react";
import ModalBorderLarge from "@assets/images/borders/ModalBorderLarge.webp";
import FaqItem from "../Faq/FaqItem";
import type { IFaq } from "@src/types/AppTypes";

const FactsModal = React.forwardRef<
  HTMLDivElement,
  {
    openFactsModal: boolean;
    setOpenFatctsModal: React.Dispatch<React.SetStateAction<boolean>>;
    facts: IFaq[];
    isFactsLoading: boolean;
  }
>(({ openFactsModal, setOpenFatctsModal, facts, isFactsLoading }, ref) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [searchVal, setSearchVal] = useState<string>("");
  const [getFacts, setGetFacts] = useState<IFaq[]>([]);

  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const header = document.getElementById("header");
    const footer = document.getElementById("footer");
    const currentScrollY = window.scrollY;
    setScrollPosition(currentScrollY);

    document.body.style.overflow = "hidden";
    document.body.style.top = `-${currentScrollY}px`;
    document.body.style.position = "fixed";
    document.body.style.width = "100%";

    if (header) {
      header.style.zIndex = "0";
    }
    if (footer) {
      footer.style.zIndex = "-1";
    }

    return () => {
      // Enable background scroll
      document.body.style.overflow = "auto";
      document.body.style.position = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollPosition);

      if (header) {
        header.style.zIndex = "";
      }
      if (footer) {
        footer.style.zIndex = "";
      }
    };
  }, []);

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (searchValue === "") {
        setGetFacts(facts);
        return;
      }
      const filterBySearch = facts.filter((item) => {
        if (
          item.question.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchValue.toLowerCase())
        ) {
          return item;
        }
      });
      setGetFacts(filterBySearch);
    },
    [facts]
  );

  useEffect(() => {
    setGetFacts(facts);
  }, [facts]);

  if (!openFactsModal) return null;

  return (
    <div className="fixed top-0 w-full h-full bg-black/50 z-10 flex items-end modal-content">
      <div
        className="modal-body h-[calc(100dvh-110px)] max-md:h-[calc(100dvh-80px)] relative p-8 pt-[4.5rem] max-md:p-4 w-full bg-gradient-to-b from-[#1D1159] to-[#004D69] rounded-tr-[3rem] rounded-br-[3rem] max-md:rounded-none"
        ref={ref}
      >
        <img
          className="absolute z-0 top-0 left-0 h-full w-full max-md:hidden"
          src={ModalBorderLarge}
          alt=""
        />
        <button
          onClick={() => setOpenFatctsModal(false)}
          className="close-button absolute top-6 right-8 cursor-pointer z-50 duration-150 hover:scale-95 max-md:hidden"
          aria-label="Close"
        >
          <i className="ri-close-circle-line text-3xl text-primary bg-[#1b1359] rounded-full"></i>
        </button>
        <div className="relative z-10 overflow-auto max-h-full pe-2">
          <div className="flex items-center justify-between mb-10 max-md:mb-6">
            <h2 className="text-[#91DBE7] text-7xl max-md:text-4xl text-start">
              Facts & <span className="text-primary">Stats</span>
            </h2>
            <button
              className="cursor-pointer z-50 duration-150 hover:scale-95 md:hidden"
              onClick={() => setOpenFatctsModal(false)}
              aria-label="Close"
            >
              <i className="ri-close-circle-line max-sm:text-3xl text-4xl text-primary"></i>
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
            className="max-w-6xl mb-10"
          >
            <div className="w-full relative que-input">
              <input
                className={`bg-primary text-black w-full text-2xl max-sm:text-base ps-10 pe-7 max-md:ps-4 max-md:pe-6 py-4 rounded-[3px_35px_25px_35px] max-sm:rounded-[3px_20px_15px_20px] bg-100 bg-no-repeat bg-[url('@assets/images/borders/inputBorder.webp')] ${
                  searchVal.trim().length > 0 ? "showcross" : ""
                }`}
                type="search"
                value={searchVal}
                onChange={(e) => {
                  setSearchVal(e.target.value);
                  handleSearch(e.target.value);
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                placeholder="Search"
              />
              {/* {searchVal && (
                  <button
                    className="bg-none absolute right-4 max-sm:right-3 top-1/2 -translate-y-1/2"
                    aria-label="close-button"
                    onClick={() => {
                      setSearchVal("");
                      setGetFacts(facts);
                    }}
                    type="button"
                  >
                    <i className="ri-close-line text-secondary text-2xl"></i>
                  </button>
                )} */}
            </div>
          </form>
          <div className="flex flex-col gap-4 max-sm:gap-2">
            {isFactsLoading ? (
              [1, 2, 3].map((val) => {
                return (
                  <div
                    className="skeleton w-full my-8 h-16 rounded-sm"
                    key={val}
                  ></div>
                );
              })
            ) : getFacts.length ? (
              getFacts.map((fact, faqIndex) => {
                return (
                  <FaqItem
                    faq={fact}
                    key={fact.id}
                    index={faqIndex}
                    openIndex={openIndex}
                    setIndex={setOpenIndex}
                  />
                );
              })
            ) : (
              <div className="text-center text-primary text-xl w-full">
                {searchVal ? "No Results Found." : "FACTS & STATS COMING SOON!"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default FactsModal;
